import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useInView } from 'react-intersection-observer';
import clsx from 'clsx';

import FullScreenSVG from '@/assets/svg/full-screen.svg';
import Image from '@/components/UI/Image/Image';

import { CustomPagination } from '@/components/UI/Pagination/Pagination';
import { useStorage } from '@/models/Storage';
import useMobile from '@/hooks/useMobile';
import { Video } from '@/components/Video/Video';
import { TagList } from '@/components/UI/TagList/TagList';
import { Like } from '@/components/UI/Like/Like';
import { analytics } from '@/utils/analytics';

import 'swiper/css';
import classes from './story.module.scss';

export const Story = observer(({ images, text, tags, preview, id, likes, pixel }) => {
    const { ref, inView, entry } = useInView();
    const swiperRef = useRef(null);

    const mobile = useMobile();

    const { setActiveStory, activeStory, addViewedStory, viewedStories } = useStorage();

    const [activeSlide, setActiveSlide] = useState(0);
    const [slidesAmount, setSlidesAmount] = useState(0);
    const handlePagination = (index: number) => {
        swiperRef.current.swiper.slideTo(index);
    };

    return (
        <div
            className={clsx(classes.story, viewedStories.includes(id) && classes.viewed)}
            ref={ref}
        >
            <div
                className={classes.content}
                onClick={() => {
                    if (!mobile) {
                        setActiveStory(id);
                        addViewedStory(id);
                    }

                    analytics(pixel);
                }}
            >
                <Swiper
                    spaceBetween={16}
                    ref={swiperRef}
                    onInit={(instance) => {
                        setActiveSlide(instance.activeIndex);
                        setSlidesAmount(instance.slides.length);
                    }}
                    onSlideChange={(swiper) => {
                        setActiveSlide(swiper.activeIndex);
                    }}
                    className={classes.slider}
                    touchMoveStopPropagation={mobile}
                >
                    {images.map((content, index) => {
                        if (content.type === 'image') {
                            return (
                                <SwiperSlide
                                    className={classes.slide}
                                    key={'stim' + content.src.src}
                                >
                                    <Image className={classes.image} src={content.src} />
                                    <button
                                        type="button"
                                        className={classes.fullscreen}
                                        onClick={() => {
                                            if (!mobile) {
                                                setActiveStory(id);
                                                addViewedStory(id);
                                            }

                                            analytics(pixel);
                                        }}
                                    >
                                        <FullScreenSVG />
                                    </button>
                                </SwiperSlide>
                            );
                        }
                        if (content.type === 'video') {
                            return (
                                <SwiperSlide className={classes.slide} key={'stvid' + content.src}>
                                    <Video
                                        source={content.src}
                                        poster={content.poster}
                                        forcedPause={
                                            activeSlide !== index || !inView || !!activeStory
                                        }
                                    />
                                    <button
                                        type="button"
                                        className={classes.fullscreen}
                                        onClick={() => {
                                            if (!mobile) {
                                                setActiveStory(id);
                                                addViewedStory(id);
                                            }

                                            analytics(pixel);
                                        }}
                                    >
                                        <FullScreenSVG />
                                    </button>
                                </SwiperSlide>
                            );
                        }
                        return null;
                    })}
                    {text.map((block, index) => {
                        return (
                            <SwiperSlide
                                key={'tsl' + id + index}
                                className={clsx(classes.slide, classes.withText)}
                            >
                                <div className={classes.text}>
                                    <div className={classes.textContainer}>{block}</div>
                                    {index === text.length - 1 && (
                                        <TagList className={'small'} customTags={tags} />
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>
            <div className={classes.controls}>
                <Like storyId={id} amount={likes} />
                {slidesAmount && (
                    <CustomPagination
                        currentIndex={activeSlide}
                        slidesAmount={slidesAmount}
                        onClick={handlePagination}
                    />
                )}
            </div>
            <p
                className={classes.title}
                onClick={() => {
                    if (!mobile) {
                        setActiveStory(id);
                        addViewedStory(id);
                    }

                    analytics(pixel);
                }}
            >
                {preview}
                <button
                    type="button"
                    className={classes.showmore}
                    onClick={() => {
                        if (!mobile) {
                            setActiveStory(id);
                            addViewedStory(id);
                        }

                        analytics(pixel);
                    }}
                >
                    Показать полностью
                </button>
            </p>
        </div>
    );
});
